import type { AxiosError } from "axios";
import msg from "components/string-table";
import { App, BuildProfile, ErrorAlert } from "main";
import {
  generateSwingSchoolsAppleAppStoreURL,
  generateSwingSchoolsGooglePlayStoreURL,
  generateSwingSchoolsLocalURL,
  generateSwingSchoolsProdURL,
  generateSwingSchoolsStagingURL,
  generateSwingSubsAppleAppStoreURL,
  generateSwingSubsGooglePlayStoreURL,
  generateSwingSubsLocalURL,
  generateSwingSubsProdURL,
  generateSwingSubsStagingURL,
  generateSwingSupportURL,
} from "swing-utils";

type ErrorLoginProps = {
  app: App;
  buildProfile: BuildProfile | undefined;
  error: ErrorData;
  platform: string;
  provider?: "cognito" | "google";
};

export function ErrorLogin(props: ErrorLoginProps) {
  const { app, buildProfile, error, platform, provider } = props;

  const errorMessage = error.response?.data.message;
  const errorLoginHref = generateErrorLoginHref(app, platform, buildProfile);

  return (
    <ErrorAlert customStyles={{ fontSize: 14 }}>
      {/*
        admin user attempting to sign into sub app:
          - Cognito - "Unmatched user type" -> user exists in Cognito pool but provided email and USER-TYPE combination is incorrect
          - Google - user exists but doesn't have required sub attributes needed for our Swing system
      */}
      {app === "swing-subs" &&
      (errorMessage?.toLowerCase().includes("unmatched user type") ||
        errorMessage?.toLowerCase().includes("missing both :sub/id and :pending-sub/id on sub")) ? (
        <span>
          {msg("LOGIN_INCORRECT_USER_TYPE_SWING_SUBS")}{" "}
          <a
            style={{ textDecoration: "none" }}
            target="_blank"
            rel="noreferrer"
            href={errorLoginHref}
          >
            {platform === "web"
              ? msg("LOGIN_INCORRECT_USER_TYPE_LABEL_WEB_SWING_SUBS")
              : msg("LOGIN_INCORRECT_USER_TYPE_LABEL_MOBILE_SWING_SUBS")}
          </a>
          .
        </span>
      ) : /*
          sub user attempting to sign into admin app:
            - Cognito - "Unmatched user type" -> user exists in Cognito pool but provided email and USER-TYPE combination is incorrect
            - Google - user exists but doesn't have required admin attributes needed for our Swing system
        */
      app === "swing-schools" &&
        (errorMessage?.toLowerCase().includes("unmatched user type") ||
          errorMessage?.toLowerCase().includes("missing :admin/id on admin")) ? (
        <span>
          {msg("LOGIN_INCORRECT_USER_TYPE_SWING_SCHOOLS")}{" "}
          <a
            style={{ textDecoration: "none" }}
            target="_blank"
            rel="noreferrer"
            href={errorLoginHref}
          >
            {platform === "web"
              ? msg("LOGIN_INCORRECT_USER_TYPE_LABEL_WEB_SWING_SCHOOLS")
              : msg("LOGIN_INCORRECT_USER_TYPE_LABEL_MOBILE_SWING_SCHOOLS")}
          </a>
          .
        </span>
      ) : (
        /**
         * Cognito - "User not found" -> user does not exist in Cognito pool
         * Google - "User not found" - un-recognized Google user
         */
        <span>
          {provider === "cognito"
            ? msg("EMAIL_FIELD_LOGIN_UNRECOGNIZED_EMAIL")
            : msg("GOOGLE_LOGIN_UNRECOGNIZED_EMAIL")}
          <a
            style={{ textDecoration: "none" }}
            target="_blank"
            rel="noreferrer"
            href={generateSwingSupportURL("/hc/en-us/requests/new")}
          >
            {msg("EMAIL_FIELD_LABEL_CONTACT_SUPPORT")}
          </a>
          .
        </span>
      )}
    </ErrorAlert>
  );
}

export type EmailLoginErrorProps = {
  detail: {
    param: {
      email: string;
      "user-type": "ADMIN" | "SUB";
    };
  };
  error: string;
  message: string;
  "trace-id": string;
};

export type ErrorResponseWithDataMessage = {
  message: string;
};

type ErrorData = AxiosError<EmailLoginErrorProps> | AxiosError<ErrorResponseWithDataMessage>;

// export for testing
export function generateErrorLoginHref(app: App, platform: string, buildProfile?: BuildProfile) {
  // Note: platform = web, ios, android - https://capacitorjs.com/docs/core-apis/web#getplatform

  if (app === "swing-subs") {
    if (platform === "android") {
      return generateSwingSchoolsGooglePlayStoreURL();
    }
    if (platform === "ios") {
      return generateSwingSchoolsAppleAppStoreURL();
    }
    if (platform === "web") {
      return buildProfile === "prod"
        ? generateSwingSchoolsProdURL()
        : buildProfile === "staging"
          ? generateSwingSchoolsStagingURL()
          : generateSwingSchoolsLocalURL();
    }
  }

  if (app === "swing-schools") {
    if (platform === "android") {
      return generateSwingSubsGooglePlayStoreURL();
    }
    if (platform === "ios") {
      return generateSwingSubsAppleAppStoreURL();
    }
    if (platform === "web") {
      return buildProfile === "prod"
        ? generateSwingSubsProdURL()
        : buildProfile === "staging"
          ? generateSwingSubsStagingURL()
          : generateSwingSubsLocalURL();
    }
  }

  throw new Error(
    `generateErrorLoginHref error: provided platform '${platform}' is not recognized!`,
  );
}
